import { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Crown, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { Standing, Game } from '../types';
import { getLeaderboard, fetchGames } from '../services/api';
import { format } from 'date-fns';

const Leaderboard = () => {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadLeaderboard();
  }, [selectedDate]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const [standingsData, gamesData] = await Promise.all([
        getLeaderboard(selectedDate),
        fetchGames(selectedDate)
      ]);
      setStandings(standingsData);
      setGames(gamesData.games);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if all games are completed for the date
  const areAllGamesCompleted = () => {
    return games.length > 0 && games.every(game => game.status === 'final');
  };

  const toggleExpanded = (entryId: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const getTeamName = (gameId: string, pick: 'home' | 'away'): string => {
    const game = games.find(g => g.id === gameId);
    if (!game) return `${pick} team`;
    return pick === 'home' ? game.homeTeam : game.awayTeam;
  };

  const getRankIcon = (rank: number) => {
    if (rank === 999) {
      return <span className="text-sm font-medium text-gray-400">Entry</span>;
    }
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Award className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-gray-500">#{rank}</span>;
    }
  };

  const getRankStyle = (rank: number) => {
    if (rank === 999) {
      return "bg-blue-50 border-blue-200";
    }
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200";
      case 2:
        return "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200";
      case 3:
        return "bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200";
      default:
        return "bg-white border-gray-200";
    }
  };

  const calculatePrizes = () => {
    const paidEntries = standings.filter(s => s.entry.paid).length;
    const totalPot = paidEntries * 1000;
    return {
      total: totalPot,
      first: Math.floor(totalPot * 0.5), // 50%
      second: Math.floor(totalPot * 0.3), // 30%
      third: Math.floor(totalPot * 0.2)   // 20%
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">NBA Pick'em Leaderboard</h1>
        <p className="mt-2 text-lg text-gray-600">
          Top performers • Entry fee: 1000 $NUTS
        </p>
        
        {/* Date Selector */}
        <div className="mt-4 flex justify-center">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input max-w-xs"
          />
        </div>
      </div>

      {/* Prize Pool Info */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-center">
          <DollarSign className="h-5 w-5 mr-2 text-green-600" />
          Prize Pool
        </h3>
        {(() => {
          const prizes = calculatePrizes();
          return (
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{prizes.total}</div>
                <div className="text-sm text-gray-600">Total $NUTS</div>
              </div>
              <div>
                <div className="text-xl font-bold text-yellow-600">{prizes.first}</div>
                <div className="text-sm text-gray-600">1st Place (50%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-500">{prizes.second}</div>
                <div className="text-sm text-gray-600">2nd Place (30%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-amber-600">{prizes.third}</div>
                <div className="text-sm text-gray-600">3rd Place (20%)</div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Leaderboard */}
      {standings.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No entries found for {selectedDate}</p>
          <p className="text-gray-400 text-sm mt-2">
            Be the first to submit an entry for this week!
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  XRPL Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tie-Break
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Picks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {standings.map((standing) => (
                <>
                  <tr key={standing.id} className={standing.rank <= 3 ? getRankStyle(standing.rank) : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getRankIcon(standing.rank)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {standing.entry.xHandle}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 font-mono">
                        {standing.entry.xrplAddress.substring(0, 12)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {standing.entry.tieBreakTotalScore}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        standing.entry.paid 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {standing.entry.paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {standing.rank === 999 ? '-' : standing.points}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleExpanded(standing.entry.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                      >
                        {standing.entry.picks?.length || 0} picks
                        {expandedEntries.has(standing.entry.id) ? 
                          <ChevronUp className="h-4 w-4 ml-1" /> : 
                          <ChevronDown className="h-4 w-4 ml-1" />
                        }
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {areAllGamesCompleted() && standing.rank <= 3 && standing.rank !== 999 && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          standing.rank === 1 ? 'bg-yellow-100 text-yellow-800' : 
                          standing.rank === 2 ? 'bg-gray-100 text-gray-800' : 
                          'bg-amber-100 text-amber-800'
                        }`}>
                          Winner
                        </span>
                      )}
                    </td>
                  </tr>
                  {/* Inline Picks Row */}
                  {expandedEntries.has(standing.entry.id) && standing.entry.picks && standing.entry.picks.length > 0 && (
                    <tr key={`${standing.entry.id}-picks`} className="bg-gray-50">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            Picks for {standing.entry.xHandle}:
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {standing.entry.picks.map((pick: any) => {
                              const game = games.find(g => g.id === pick.gameId);
                              const isCorrect = game?.winner === pick.pick;
                              const isCompleted = game?.status === 'final';
                              
                              return (
                                <div 
                                  key={pick.gameId} 
                                  className={`p-2 rounded text-xs border ${
                                    isCompleted 
                                      ? isCorrect 
                                        ? 'bg-green-50 border-green-200 text-green-800' 
                                        : 'bg-red-50 border-red-200 text-red-800'
                                      : 'bg-gray-50 border-gray-200 text-gray-600'
                                  }`}
                                >
                                  <div className="font-medium">
                                    {getTeamName(pick.gameId, pick.pick)}
                                  </div>
                                  {game && (
                                    <div className="text-xs opacity-75">
                                      vs {pick.pick === 'home' ? game.awayTeam : game.homeTeam}
                                    </div>
                                  )}
                                  {isCompleted && (
                                    <div className="font-medium">
                                      {isCorrect ? '✓ Correct' : '✗ Wrong'}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contest Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-600">
          Rankings based on correct picks with tie-breaker determining final order.
          <br />
          Tie-breaker: Closest prediction to total combined score of final NFL game.
        </p>
      </div>
    </div>
  );
};

export default Leaderboard;
